import {
  Machine,
  MachineConfig,
  MachineOptions,
  assign,
  sendParent,
} from 'xstate';
import {
  ModelScreenshot,
  CreateScreenshotDocument,
  GetScreenshotsDocument,
  GetSignedUrlDocument,
} from 'src/generated/graphql';
import generateCameraData from 'src/utils/generateCameraData';
import constants from 'src/constants';
import gqlClient from 'src/apolloClient';
import jszip from 'jszip';

const { ASPECT_RATIO } = constants.screenshot;

interface ModelData {
  geometry: any;
  metadata: any;
}

export interface ScreenshotUploadContext {
  jobId: number;
  tempUuid: string;
  modelData?: ModelData;
  imageData?: string;
  cameraData?: any;
  archiveBlob?: string;
  screenshotBlob?: string;
  signedArchiveUrl?: string;
  signedScreenshotUrl?: string;
  uploadKey?: string;
}

interface ScreenshotUploadSchema {
  states: {
    gettingSignedUrlsAndUploadKey: {};
    archivingData: {};
    uploadingArchives: {};
    creatingScreenshot: {};
    done: {};
    failed: {};
  };
}

export interface ScreenshotUploadEvent {
  type: string;
}

export const options: Partial<MachineOptions<
  ScreenshotUploadContext,
  ScreenshotUploadEvent
>> = {
  actions: {
    assignModelData: assign((_) => {
      // @ts-ignore
      const model = Hover.Machete.viewer.modelContainer.models[0];
      const metadata = JSON.stringify(model.exportAsMacheteJSON());
      const geometry = JSON.stringify(model.exportAsModelJSONv3());

      return {
        modelData: {
          geometry,
          metadata,
        },
      };
    }),
    assignImageAndCameraData: assign((_) => {
      // @ts-ignore
      const { viewer, Utils } = Hover.Machete;
      const { camera, renderer } = viewer;
      const imageData = Utils.screenshot(renderer).toDataURL();
      const cameraData = generateCameraData(camera);

      return {
        imageData,
        cameraData,
      };
    }),
    removeScreenshotFromCache: async (context) => {
      const data: any = gqlClient.readQuery({
        query: GetScreenshotsDocument,
        variables: { jobId: context.jobId },
      });
      const screenshots = data.screenshots.filter(
        (s: ModelScreenshot) => s.id !== context.tempUuid,
      );
      await gqlClient.writeQuery({
        query: GetScreenshotsDocument,
        variables: { jobId: context.jobId },
        data: { screenshots },
      });
    },
    updateCacheWithScreenshot: async (context) => {
      const screenshot = {
        __typename: 'ModelScreenshot',
        id: context.tempUuid,
        placeholderUrl: context.imageData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        rendering: {
          __typename: 'Rendering',
          id: context.tempUuid,
          state: 'queued',
          percentDone: 0,
          versionUrls: null,
        },
      };
      const data: any = gqlClient.readQuery({
        query: GetScreenshotsDocument,
        variables: { jobId: context.jobId },
      });
      const screenshots = [screenshot, ...data.screenshots];
      await gqlClient.writeQuery({
        query: GetScreenshotsDocument,
        variables: { jobId: context.jobId },
        data: { screenshots },
      });
    },
    receiveEventData: assign((_, event: any) => event.data),
    notifyDone: sendParent((context: ScreenshotUploadContext) => ({
      type: 'CAPTURE_SUCCESS',
      id: context.tempUuid,
    })),
    notifyError: sendParent((context: ScreenshotUploadContext, event: any) => ({
      type: 'CAPTURE_FAIL',
      error: event.data,
      id: context.tempUuid,
    })),
  },
  services: {
    getSignedUrlsAndUploadKey: async (_) => {
      const res = await gqlClient.mutate({
        mutation: GetSignedUrlDocument,
      });

      const {
        archiveUrl: signedArchiveUrl,
        screenshotUrl: signedScreenshotUrl,
        uploadKey,
      } = res.data.createSignedUrlForScreenshot;

      return {
        signedArchiveUrl,
        signedScreenshotUrl,
        uploadKey,
      };
    },
    archiveData: async (context) => {
      const archive = new jszip();
      archive.file('metadata.json', context.modelData!.metadata as string);
      archive.file('geometry.json', context.modelData!.geometry as string);
      const archiveBlob = await archive.generateAsync({ type: 'blob' });
      const screenshotBlob = await (
        await fetch(context.imageData as string)
      ).blob();

      return { archiveBlob, screenshotBlob };
    },
    uploadArchives: async (context) => {
      const archiveFile = new File(
        [(context.archiveBlob as unknown) as Blob],
        'file.zip',
      );
      const screenshotFile = new File(
        [(context.screenshotBlob as unknown) as Blob],
        'file.png',
      );

      const [archiveResponse, screenshotResponse] = await Promise.all([
        fetch(context.signedArchiveUrl as string, {
          method: 'PUT',
          body: archiveFile,
        }),
        fetch(context.signedScreenshotUrl as string, {
          method: 'PUT',
          body: screenshotFile,
        }),
      ]);

      if (
        !(archiveResponse.status === 200 && screenshotResponse.status === 200)
      ) {
        throw Error('Uploading failed!');
      }
    },
    createScreenshot: async (context) =>
      gqlClient.mutate({
        mutation: CreateScreenshotDocument,
        variables: {
          aspectRatio: ASPECT_RATIO,
          camera: context.cameraData,
          jobId: context.jobId,
          uploadKey: context.uploadKey,
        },
      }),
  },
};

export const config: MachineConfig<
  ScreenshotUploadContext,
  ScreenshotUploadSchema,
  ScreenshotUploadEvent
> = {
  id: 'takeScreenshot',
  entry: ['assignModelData', 'assignImageAndCameraData'],
  initial: 'gettingSignedUrlsAndUploadKey',
  context: {
    jobId: 0,
    tempUuid: '',
  },
  states: {
    gettingSignedUrlsAndUploadKey: {
      entry: 'updateCacheWithScreenshot',
      invoke: {
        src: 'getSignedUrlsAndUploadKey',
        onDone: {
          target: 'archivingData',
          actions: 'receiveEventData',
        },
        onError: 'failed',
      },
    },
    archivingData: {
      invoke: {
        src: 'archiveData',
        onDone: {
          target: 'uploadingArchives',
          actions: 'receiveEventData',
        },
        onError: 'failed',
      },
    },
    uploadingArchives: {
      invoke: {
        src: 'uploadArchives',
        onDone: {
          target: 'creatingScreenshot',
          actions: 'receiveEventData',
        },
        onError: 'failed',
      },
    },
    creatingScreenshot: {
      invoke: {
        src: 'createScreenshot',
        onDone: {
          target: 'done',
          actions: 'notifyDone',
        },
        onError: 'failed',
      },
    },
    done: {
      type: 'final',
    },
    failed: {
      type: 'final',
      entry: ['notifyError', 'removeScreenshotFromCache'],
    },
  },
};

const screenshotUploadMachine = Machine<
  ScreenshotUploadContext,
  ScreenshotUploadSchema,
  ScreenshotUploadEvent
>(config, options);

export const generateScreenshotUploadMachine = (
  jobId: number,
  tempUuid: string,
) => {
  return screenshotUploadMachine.withContext({ jobId, tempUuid });
};

export default screenshotUploadMachine;
